import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { BoardView } from '@/components/kanban/board-view'
import { IssueDataTable, TableSkeleton } from '@/components/tables/data-table'
import { ISSUE_COLUMNS } from '@/components/tables/issue-columns'
import { StageIcon } from '@/components/common/badges'
import { ViewModeToggle } from '@/components/common/view-mode-toggle'
import { useViewMode } from '@/hooks/use-view-mode'
import { ViewError } from '@/components/common/query-states'
import { ViewHeader } from '@/components/shell/view-header'
import { useOpenIssueSheet } from '@/hooks/use-ui-state'
import { useViewFilters } from '@/hooks/use-view-filters'
import { useSortPreference } from '@/hooks/use-sort-preference'
import { fieldByName, useIssueFields, useIssues } from '@/lib/query'
import { useSetIssueField } from '@/lib/mutations'
import { Button } from '@/components/ui/button'
import { SortMenu } from '@/components/common/sort-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { areaLabel, availableStages } from '@/lib/stage'
import { byDefaultOrder, comparatorFor } from '@/lib/sort'
import { columnTotal, groupIntoColumns } from '@/lib/grouping'
import { useTeams } from '@/hooks/use-teams'

const TEAM_STAGE_ORDER = ['Backlog', 'Ready', 'In progress', 'In review', 'Merged', 'Blocked', 'Waiting']

// The route loader has already 404'd invalid areas.
export default function TeamArea() {
  const { area } = useParams()
  return <TeamBoard area={area!} />
}

function readMyTeam(): string | null {
  try {
    return window.localStorage.getItem('n3o-work-last-area')
  } catch {
    return null
  }
}

function TeamBoard({ area }: { area: string }) {
  const navigate = useNavigate()
  const query = useIssues()
  const issues = query.data
  const openIssue = useOpenIssueSheet()
  const chipFilter = useViewFilters()
  const [sort] = useSortPreference()
  const [view, setView] = useViewMode()
  const setField = useSetIssueField()
  const teams = useTeams()
  const fields = useIssueFields().data
  const teamStages = useMemo(
    () => availableStages(TEAM_STAGE_ORDER, fieldByName(fields, 'Stage')?.options.map((o) => o.name)),
    [fields],
  )
  // Browsing another team's board must NOT redefine "my team" — only the
  // picker or this explicit action writes the preference.
  const [myTeam, setMyTeam] = useState(readMyTeam)

  const adoptTeam = () => {
    try {
      window.localStorage.setItem('n3o-work-last-area', area)
    } catch {
      /* ignore */
    }
    setMyTeam(area)
  }

  const itemsByColumn = useMemo(() => {
    const label = `area/${area}`
    // Only stages that have a column — dumping Triage/Released strays into
    // "Backlog" would misstate their stage on a stage-grouped board.
    const pool = chipFilter.apply(
      (issues ?? []).filter(
        (i) => i.labels.includes(label) && i.stage !== null && teamStages.includes(i.stage),
      ),
    )
    return groupIntoColumns(pool, teamStages, (i) => i.stage!, comparatorFor(sort, byDefaultOrder))
  }, [issues, chipFilter, area, teamStages, sort])

  const total = columnTotal(itemsByColumn)

  return (
    <div className="flex h-full flex-col">
      <ViewHeader
        title={`${areaLabel(area)} team`}
        meta={`${total} open items · grouped by Stage`}
        chips={['priority', 'assignee']}
        toolbar={
          <div className="flex items-center gap-1.5">
            <ViewModeToggle mode={view} onChange={setView} />
            {view === 'board' && <SortMenu />}
            <span className="text-faint-foreground">Team</span>
            <Select value={area} onValueChange={(a) => navigate(`/team/${a}`)}>
              <SelectTrigger size="sm" className="h-6 w-28 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(teams.length ? teams : [area]).map((a) => (
                  <SelectItem key={a} value={a}>
                    {areaLabel(a)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {myTeam !== area && (
              <Button variant="ghost" size="sm" className="h-6 px-2 text-[11px]" onClick={adoptTeam}>
                Set as my team
              </Button>
            )}
          </div>
        }
      />
      <div className="min-h-0 flex-1">
        {view === 'table' ? (
          query.isError ? (
            <ViewError what="issues" error={query.error} onRetry={() => query.refetch()} />
          ) : query.isPending ? (
            <TableSkeleton />
          ) : (
            <IssueDataTable
              issues={[...itemsByColumn.values()].flat()}
              columns={ISSUE_COLUMNS}
              sortable
              bulkKind="work"
              emptyTitle={`No open ${areaLabel(area)} issues`}
            />
          )
        ) : (
          <BoardView
            query={query}
            skeletonColumns={5}
            columns={teamStages.map((stage) => ({
              key: stage,
              title: stage,
              glyph: <StageIcon stage={stage} />,
            }))}
            itemsByColumn={itemsByColumn}
            onCardClick={openIssue}
            onMoveAcross={(issue, pos) =>
              setField.mutate({ issue, fieldName: 'Stage', value: pos.toColumn })
            }
          />
        )}
      </div>
    </div>
  )
}
